/**
 * 图表基类 HOC
 * 简化设计：只处理数据绑定，其余配置直接透传给 Ant Design Charts
 */
import React, { useMemo, useRef } from 'react';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import type { BaseChartProps } from './types';

export interface WithChartOptions {
  /** 图表类型名称，用于日志 */
  chartType: string;
  /** 默认高度 */
  defaultHeight?: number;
  /** 默认示例数据 (设计器中展示) */
  defaultData?: any;
}

/**
 * 解析可能是 JSON 字符串的属性
 */
export function parseJsonProp(value: any): any {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  if (typeof value === 'string') {
    try {
      const trimmed = value.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        return JSON.parse(trimmed);
      }
    } catch (e) {
      // 解析失败返回原值
    }
  }
  return value;
}

/**
 * 处理布尔值（可能是字符串 'true'/'false'）
 */
export function parseBoolProp(value: any, defaultValue?: boolean): boolean | undefined {
  if (value === undefined || value === null || value === '') {
    return defaultValue;
  }
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return defaultValue;
}

/**
 * Ant Design Charts 会 deepAssign 原地修改 config；Immer 冻结的 props 会报错。
 */
function cloneChartConfig<T>(value: T): T {
  if (value === undefined || value === null) {
    return value;
  }
  if (typeof structuredClone === "function") {
    try {
      return structuredClone(value);
    } catch {
      // fall through
    }
  }
  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * 图表基类 HOC
 * 封装数据处理逻辑，其余配置直接透传
 */
export function withBaseChart<P extends BaseChartProps>(
  ChartComponent: React.ComponentType<any>,
  options: WithChartOptions
) {
  const { chartType, defaultHeight = 300, defaultData = [] } = options;

  const BaseChartWrapper: React.FC<P> = (props) => {
    const {
      // 基础组件 props（不传给图表）
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      id: _id,
      sessionId,
      develop,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      style: _style,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      className: _className,
      
      // 数据相关
      dataSet,
      dataSetField,
      data: propData,
      
      // 布局键（G2 顶层，落库直写 props.*）
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      common: _legacyCommon,
      height: propHeight,
      width: propWidth,
      autoFit: propAutoFit,
      padding: propPadding,
      margin: propMargin,
      inset: propInset,
      // 需要解析的 JSON 配置
      title,
      legend,
      label,
      tooltip,
      animate,
      scale,
      interaction,
      annotations,
      axis,
      scrollbar,
      slider,
      theme,
      state,
      
      // 事件配置
      events,
      
      // 其余配置直接透传
      ...restProps
    } = props as any;

    const chartRef = useRef<any>(null);
    const getDataSet = useAndStore((state) => state.getDataSet);
    const addCommand = useAndStore((state) => state.addCommand);

    const dataSetStoreKey = useMemo(
      () => getDataSetStoreKey(sessionId, dataSet),
      [sessionId, dataSet],
    );
    const boundField =
      dataSetField && String(dataSetField).trim() !== "" ? String(dataSetField) : "";

    // 订阅 store 槽位，dataset refresh 后 chartData 随 store 更新（与 And* useStoreData 一致）
    const dataChunk = useAndStore((s) =>
      dataSetStoreKey ? s.dataSets[dataSetStoreKey] : undefined,
    );

    // ===== 数据处理 =====
    const chartData = useMemo(() => {
      if (dataSetStoreKey) {
        const dsData = getDataSet(dataSetStoreKey, boundField);
        if (dsData !== undefined) return dsData;
      }

      // 解析 JSON 字符串或直接使用 props.data（设计态 / 未绑 store 时的 fallback）
      const parsed = parseJsonProp(propData);
      // 支持数组格式（大多数图表）和对象格式（如 Gauge 的 {percent: 0.7}）
      if (parsed !== undefined) return parsed;

      // 设计器模式使用默认数据
      if (develop) return defaultData;

      return [];
    }, [propData, dataSetStoreKey, boundField, getDataSet, dataChunk, develop, defaultData]);

    // ===== 事件处理 =====
    const handleChartReady = (chart: any) => {
      chartRef.current = chart;
      
      // 设计器模式不注册事件
      if (!develop && sessionId) {
        // 处理 events JSON 配置
        // 直接使用完整事件名，如: { "element:click": "pieClick" }
        const eventsConfig = parseJsonProp(events);
        if (eventsConfig && typeof eventsConfig === 'object') {
          Object.entries(eventsConfig).forEach(([eventName, actionName]) => {
            if (typeof actionName === 'string') {
              chart.on(eventName, (ev: any) => {
                addCommand(sessionId, {
                  actionName,
                  arg: ev,
                  timestamp: Date.now(),
                });
              });
            }
          });
        }
      }
      
      // 调用外部 onReady
      (restProps as any).onReady?.(chart);
    };

    const autoFitEnabled = parseBoolProp(propAutoFit, true);

    const layoutConfig: Record<string, unknown> = {};
    if (propPadding !== undefined) layoutConfig.padding = parseJsonProp(propPadding) ?? propPadding;
    if (propMargin !== undefined) layoutConfig.margin = propMargin;
    if (propInset !== undefined) layoutConfig.inset = propInset;
    if (!autoFitEnabled) {
      if (propHeight !== undefined) layoutConfig.height = propHeight;
      if (propWidth !== undefined) layoutConfig.width = propWidth;
    }

    // ===== 组装最终配置 =====
    const finalConfig: Record<string, any> = {
      data: chartData,
      autoFit: autoFitEnabled,
      ...layoutConfig,
      // 设计器模式禁用动画
      animate: develop ? false : parseJsonProp(animate),
      onReady: handleChartReady,
      ...restProps,
    };

    // 仅关闭自适应时补默认高度；autoFit=true 且无显式 height 时不注入像素
    if (!autoFitEnabled && finalConfig.height == null) {
      finalConfig.height = defaultHeight;
    }

    // 解析 JSON 配置（只添加非空值）
    const titleConfig = parseJsonProp(title);
    if (titleConfig !== undefined) finalConfig.title = titleConfig;
    
    const legendConfig = parseJsonProp(legend);
    if (legendConfig !== undefined) finalConfig.legend = legendConfig;
    
    const labelConfig = parseJsonProp(label);
    if (labelConfig !== undefined) finalConfig.label = labelConfig;
    
    const tooltipConfig = parseJsonProp(tooltip);
    if (tooltipConfig !== undefined) finalConfig.tooltip = tooltipConfig;
    
    const scaleConfig = parseJsonProp(scale);
    if (scaleConfig !== undefined) finalConfig.scale = scaleConfig;
    
    const interactionConfig = parseJsonProp(interaction);
    if (interactionConfig !== undefined) finalConfig.interaction = interactionConfig;

    const annotationsConfig = parseJsonProp(annotations);
    if (annotationsConfig !== undefined) finalConfig.annotations = annotationsConfig;

    const axisConfig = parseJsonProp(axis);
    if (axisConfig !== undefined) finalConfig.axis = axisConfig;

    const scrollbarConfig = parseJsonProp(scrollbar);
    if (scrollbarConfig !== undefined) finalConfig.scrollbar = scrollbarConfig;

    const sliderConfig = parseJsonProp(slider);
    if (sliderConfig !== undefined) finalConfig.slider = sliderConfig;

    
    const themeConfig = parseJsonProp(theme);
    if (themeConfig !== undefined) finalConfig.theme = themeConfig;
    
    const stateConfig = parseJsonProp(state);
    if (stateConfig !== undefined) finalConfig.state = stateConfig;

    return <ChartComponent {...cloneChartConfig(finalConfig)} />;
  };

  BaseChartWrapper.displayName = `withBaseChart(${chartType})`;
  
  return BaseChartWrapper;
}

export default withBaseChart;
